/**
 * @fileoverview Live preview component with drag-and-drop functionality for the fundraiser builder.
 *
 * Provides real-time preview of fundraiser layout with draggable sections, zone-based layouts,
 * and responsive design. Handles section positioning, drag operations, and layout management.
 *
 * Key Features:
 * - Drag-and-drop section reordering between zones
 * - Live preview with custom colors and styling
 * - Responsive section rendering based on zone type
 * - Layout template support (1-5 different layouts)
 *
 * @author Fundraiser Builder Team
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  DragOverEvent,
  rectIntersection,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { DraggableSection } from "./DraggableSection";
import { DropZone } from "./DropZone";
import Header from "../../Header";
import { ProgressSection } from "../../ProgressSection";
import { ImpactStats } from "../../ImpactStats";
import { MissionSection } from "../../MissionSection";
import { ImpactSection } from "../../ImpactSection";
import { MediaSection } from "../../MediaSection";
import { PromoterLeaderboard } from "../../PromoterLeaderboard";
import { RecentDonations } from "../../RecentDonations";
import { CallToAction } from "../../CallToAction";
import { DonationCapture } from "../../donation-capture/DonationCapture";
import { ColorSwatches, DonationFormType, IconType } from "../../../enums";
import { ISectionVisibility } from "../../../interfaces";
import { AuthService } from "@mindmemobile/mm-shared-global";
import { colorTemplates } from "./designConstants";
import { FundraiserCustomColorConfig } from "../../../models";

const PreviewContainer = styled.div`
  background: #f8fafc;
  margin-left: 0;
  border: 2px solid #e2e8f0;
  overflow: visible;
  height: auto;
  min-height: fit-content;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 0;
  width: 100%;
  .description-design-preview {
    background: #ffffff;
    border-radius: 16px;
    padding: 24px;
    border: 1px solid #f1f5f9;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
  .sponsor-design-preview {
    width: 100%;
    padding: 0;
    margin: 16px 0;
  }
`;

const PreviewFrame = styled.div`
  width: 100%;
  overflow: visible;
  position: relative;
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  border-radius: 8px;
  height: auto;
  min-height: 100%;
`;

const PreviewContent = styled.div`
  width: 100%;
  height: auto;
  transform: scale(1);
  transform-origin: top center;
  overflow: visible;
  position: relative;
  min-height: fit-content;
  margin: 0 auto;
  background: #f8fafc;
`;

const PreviewWrapper = styled.div<{ backgroundColor?: string }>`
  width: 100%;
  background: ${(props) => props.backgroundColor || "#ffffff"};
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 0 auto;
  height: auto;
  min-height: fit-content;
`;

const SectionContainer = styled.div<{ backgroundColor?: string }>`
  background: ${(props) => props.backgroundColor || "#ffffff"};
  min-height: 100vh;
`;

const ContentWrapper = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 1;
`;

// Four-zone layout containers
const TopZone = styled.div<{ zoneWidth?: number }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 32px 0;
  min-height: 60px;
`;

const MiddleZoneContainer = styled.div<{
  leftWidth?: number;
  rightWidth?: number;
}>`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 32px;
  margin: 32px 0;
  min-height: 200px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const LeftZone = styled.div<{ zoneWidth?: number }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: 200px;
`;

const RightZone = styled.div<{ zoneWidth?: number }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: 200px;
`;

const BottomZone = styled.div<{ zoneWidth?: number }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px 0 0 0;
  padding-bottom: 20px;
  min-height: 60px;
`;

// Enhanced drag overlay styling
const CompactDragOverlay = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border: 2px solid #3b82f6;
  border-radius: 8px;
  padding: 4px 12px;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
  font-size: 10px;
  font-weight: 600;
  color: #3b82f6;
  min-width: 60px;
  max-width: 100px;
  text-align: center;
  opacity: 1;
  transform: rotate(1deg) scale(0.7);
  pointer-events: none;
  z-index: 99999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SectionDragPreview = styled.div`
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  border: 3px solid #3b82f6;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(59, 130, 246, 0.5);
  pointer-events: none;
  z-index: 999999;
  opacity: 0.95;
  width: 400px;
  height: 250px;
  overflow: hidden;
  position: relative;

  /* Offset to position near cursor */
  transform: translate(-200px, -125px);

  /* Add section label overlay */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      rgba(59, 130, 246, 0.1) 0%,
      rgba(59, 130, 246, 0.05) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const SectionPreviewLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(59, 130, 246, 0.9);
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  z-index: 2;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionPreviewContent = styled.div`
  position: relative;
  z-index: 0;
  transform: scale(0.65);
  transform-origin: top left;
  width: 615px; /* 400px / 0.65 to get proper final width */
  height: 385px; /* 250px / 0.65 to get proper final height */

  /* Hide interactive elements in preview */
  button,
  input,
  textarea,
  select {
    pointer-events: none !important;
    cursor: default !important;
  }
`;
const getSectionName = (sectionId: string): string => {
  // Get section visibility from builder data for dynamic progress text
  const sectionVisibility =
    fundraiserStore.builderData?.sectionVisibility ||
    ({} as ISectionVisibility);
  const fundraiserProgressVisible =
    sectionVisibility.fundraiserProgress !== false;
  const promoterProgressVisible = sectionVisibility.promoterProgress !== false;

  const sectionNames: Record<string, string> = {
    header: "Header",
    fundraiserProgress:
      fundraiserProgressVisible && promoterProgressVisible
        ? "Progress"
        : "Fundraiser Progress",
    promoterProgress:
      fundraiserProgressVisible && promoterProgressVisible
        ? "Progress"
        : "Promoter Progress",
    stats: "Stats",
    mission: "Mission",
    impact: "Impact",
    media: "Media",
    donation: "Donation Form",
    promoters: "Leaderboard",
    recent: "Recent Donations",
    cta: "Call to Action",
    description: "Description",
  };
  return sectionNames[sectionId] || sectionId;
};

export const DesignPreview: React.FC = observer(() => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragSnapshot, setDragSnapshot] = useState<
    typeof fundraiserStore.layoutZones | null
  >(null);

  // Mock function for donate button in preview mode
  const mockDonateClick = () => {
    // No-op function for preview mode
  };

  // Get zone widths from current layout
  const getZoneWidths = () => {
    const selectedLayoutId =
      fundraiserStore.builderData?.selectedLayout || "Layout1";
    const layoutTemplates = {
      Layout1: { topZone: 100, leftZone: 67, rightZone: 33, bottomZone: 100 },
      Layout2: { topZone: 100, leftZone: 33, rightZone: 67, bottomZone: 100 },
      Layout3: { topZone: 100, leftZone: 50, rightZone: 50, bottomZone: 100 },
      Layout4: { topZone: 0, leftZone: 67, rightZone: 33, bottomZone: 100 },
      Layout5: { topZone: 100, leftZone: 0, rightZone: 0, bottomZone: 0 },
    };

    return (
      layoutTemplates[selectedLayoutId] || {
        topZone: 100,
        leftZone: 67,
        rightZone: 33,
        bottomZone: 100,
      }
    );
  };

  const zoneWidths = getZoneWidths();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { builderData } = fundraiserStore;
  // Debug logging to see if colors are updating
  const sectionVisibility =
    builderData.sectionVisibility || ({} as ISectionVisibility);
  const selectedLayout = builderData.selectedLayout || "progress-first";
  const donationFormType =
    builderData.donationFormType || DonationFormType.Embedded;

  // Get current layout ID for consistent use throughout component
  const currentLayoutId = builderData.selectedLayout || "Layout1";

  // Get sections assigned to each zone
  const getZoneSections = (zone: keyof typeof fundraiserStore.layoutZones) => {
    // During drag operations, use the immutable snapshot to prevent reordering
    if (isDragging && dragSnapshot) {
      return dragSnapshot[zone].filter((sectionId) => {
        // Filter by visibility
        if (sectionVisibility[sectionId] === false) return false;

        // Filter out donation section when donation form type is popup
        if (
          sectionId === "donation" &&
          donationFormType !== DonationFormType.Embedded
        )
          return false;

        // Filter out sponsor section when no banner image
        if (
          sectionId === "sponsor" &&
          !fundraiserStore.builderData?.bannerImage
        )
          return false;

        return true;
      });
    }

    // Normal operation (not dragging) - get current sections without auto-placement
    let zoneSections = [...fundraiserStore.layoutZones[zone]]; // Create copy to avoid mutations

    // Handle sponsor section visibility - only filter based on banner image existence
    // Remove automatic placement logic to allow manual drag operations (fix for bug 2)
    if (!fundraiserStore.builderData?.bannerImage) {
      zoneSections = zoneSections.filter((id) => id !== "sponsor");
    }

    // Filter out donation section when donation form type is popup (fix for popup drop zone issue)
    if (donationFormType !== DonationFormType.Embedded) {
      zoneSections = zoneSections.filter((id) => id !== "donation");
    }

    return zoneSections.filter((sectionId) => {
      return sectionVisibility[sectionId] !== false;
    });
  };

  const getAllDraggableSections = () => {
    // During drag operations, use the snapshot to get consistent section list
    const layoutZones =
      isDragging && dragSnapshot ? dragSnapshot : fundraiserStore.layoutZones;
    const allSections = Object.values(layoutZones).flat();
    return allSections.filter(
      (sectionId) => sectionVisibility[sectionId] !== false
    );
  };

  // Memoize zone sections to avoid multiple calculations and ensure consistency
  const topZoneSections = getZoneSections("topZone");
  const leftZoneSections = getZoneSections("leftZone");
  const rightZoneSections = getZoneSections("rightZone");
  const bottomZoneSections = getZoneSections("bottomZone");

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    setIsDragging(true);

    // Create a snapshot of current zone state to prevent modifications during drag
    setDragSnapshot({
      topZone: [...fundraiserStore.layoutZones.topZone],
      leftZone: [...fundraiserStore.layoutZones.leftZone],
      rightZone: [...fundraiserStore.layoutZones.rightZone],
      bottomZone: [...fundraiserStore.layoutZones.bottomZone],
    });
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Don't update store state during drag over to prevent unnecessary re-renders
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setIsDragging(false);
    setDragSnapshot(null);

    if (!over || active.id === over.id) {
      return;
    }

    const activeSection = active.id as string;
    const overTarget = over.id as string;

    // Prevent donation form from being dropped in RIGHT zone
    if (activeSection === "donation" && overTarget === "rightZone-dropzone") {
      return;
    }

    let stateChanged = false;

    // Handle dropping on drop zones
    if (overTarget.endsWith("-dropzone")) {
      const targetZone = overTarget.replace(
        "-dropzone",
        ""
      ) as keyof typeof fundraiserStore.layoutZones;

      // Handle sponsor section movement (fix for bug 2)
      if (activeSection === "sponsor") {
        // Get current zone of the sponsor section
        const currentZone = fundraiserStore.findSectionZone(activeSection);

        if (currentZone && currentZone.zoneId !== targetZone) {
          // Remove from current zone
          const sourceZone = fundraiserStore.layoutZones[currentZone.zoneId];
          const activeIndex = sourceZone.indexOf(activeSection);
          if (activeIndex !== -1) {
            sourceZone.splice(activeIndex, 1);
          }

          // Add to target zone at the end
          const targetZoneSections = fundraiserStore.layoutZones[targetZone];
          if (!targetZoneSections.includes(activeSection)) {
            fundraiserStore.layoutZones[targetZone] = [
              ...targetZoneSections,
              activeSection,
            ];
          }
        }
        return;
      }

      // Get current zone of the section being dragged
      const currentZone = fundraiserStore.findSectionZone(activeSection);

      // Only move if the section is actually changing zones
      if (currentZone && currentZone.zoneId !== targetZone) {
        // Remove from current zone
        const sourceZone = fundraiserStore.layoutZones[currentZone.zoneId];
        const activeIndex = sourceZone.indexOf(activeSection);
        if (activeIndex !== -1) {
          sourceZone.splice(activeIndex, 1);
          stateChanged = true;
        }

        // Add to target zone using layout configuration order
        const targetZoneSections = fundraiserStore.layoutZones[targetZone];
        const selectedLayoutId =
          fundraiserStore.selectedFundraiserDetails?.selectedLayout ||
          "Layout1";

        // Get the designated order for this zone from layout configuration
        const LAYOUT_CONFIGURATIONS = {
          Layout1: {
            topZone: [
              "mission",
              "impact",
              "fundraiserProgress",
              "promoterProgress",
              "stats",
            ],
            leftZone: ["media", "donation"],
            rightZone: ["cta", "promoters", "recent"],
            bottomZone: ["description", "sponsor"],
          },
          Layout2: {
            topZone: [
              "mission",
              "impact",
              "fundraiserProgress",
              "promoterProgress",
              "stats",
            ],
            leftZone: ["cta", "promoters", "recent"],
            rightZone: ["media", "donation"],
            bottomZone: ["description", "sponsor"],
          },
          Layout3: {
            topZone: [
              "mission",
              "impact",
              "fundraiserProgress",
              "promoterProgress",
              "stats",
            ],
            leftZone: ["media", "donation"],
            rightZone: ["cta", "promoters", "recent"],
            bottomZone: ["description", "sponsor"],
          },
          Layout4: {
            topZone: [],
            leftZone: [
              "mission",
              "media",
              "fundraiserProgress",
              "promoterProgress",
              "stats",
              "donation",
            ],
            rightZone: ["impact", "cta", "promoters", "recent"],
            bottomZone: ["description", "sponsor"],
          },
          Layout5: {
            topZone: [
              "mission",
              "impact",
              "media",
              "cta",
              "fundraiserProgress",
              "donation",
              "stats",
              "promoters",
              "recent",
              "description",
              "sponsor",
            ],
            leftZone: [],
            rightZone: [],
            bottomZone: [],
          },
        };

        const config =
          LAYOUT_CONFIGURATIONS[selectedLayoutId] ||
          LAYOUT_CONFIGURATIONS.Layout1;
        const designatedOrder = config[targetZone] || [];

        // Find correct insertion point based on designated order
        let insertIndex = targetZoneSections.length; // Default to end

        const sectionOrderIndex = designatedOrder.indexOf(activeSection);
        if (sectionOrderIndex !== -1) {
          // Section has a designated position, find where to insert
          for (let i = 0; i < targetZoneSections.length; i++) {
            const existingSectionId = targetZoneSections[i];
            const existingOrderIndex =
              designatedOrder.indexOf(existingSectionId);

            if (
              existingOrderIndex > sectionOrderIndex ||
              existingOrderIndex === -1
            ) {
              insertIndex = i;
              break;
            }
          }
        }

        // Insert at the calculated position
        targetZoneSections.splice(insertIndex, 0, activeSection);
      }
      return;
    }

    // Handle reordering within same zone
    const findSectionZone = (
      sectionId: string
    ): keyof typeof fundraiserStore.layoutZones | null => {
      for (const [zoneKey, sections] of Object.entries(
        fundraiserStore.layoutZones
      )) {
        if (sections.includes(sectionId)) {
          return zoneKey as keyof typeof fundraiserStore.layoutZones;
        }
      }
      return null;
    };

    const activeZone = findSectionZone(activeSection);
    const overZone = findSectionZone(overTarget);

    if (activeZone && overZone && activeZone === overZone) {
      stateChanged = fundraiserStore.reorderSectionsInZone(
        activeZone,
        activeSection,
        overTarget
      );
    } else if (activeZone && overZone && activeZone !== overZone) {
      // Remove from source zone
      const sourceZone = fundraiserStore.layoutZones[activeZone];
      const activeIndex = sourceZone.indexOf(activeSection);
      if (activeIndex !== -1) {
        sourceZone.splice(activeIndex, 1);
        stateChanged = true;
      }

      // Add to target zone at the position of the over element
      const targetZone = fundraiserStore.layoutZones[overZone];
      const overIndex = targetZone.indexOf(overTarget);
      targetZone.splice(overIndex, 0, activeSection);
    }

    // Only trigger re-render if state actually changed
  };

  const renderSection = (sectionId: string, zoneType?: string) => {
    // Get current zone for this section
    const currentZone = fundraiserStore.findSectionZone(sectionId);
    const isInRightZone = currentZone?.zoneId === "rightZone";
    const isInTopOrBottomZone = zoneType === "full-width";

    // Prevent unnecessary re-renders during drag operations for non-dragged sections
    const isBeingDragged = activeId === sectionId;

    switch (sectionId) {
      case "header":
        return (
          <Header
            isPreview={true}
            customColors={builderData.getCustomColors()}
          />
        );

      case "fundraiserProgress":
      case "promoterProgress":
        // Smart rendering to prevent duplicates
        if (
          sectionVisibility.fundraiserProgress &&
          sectionVisibility.promoterProgress
        ) {
          // Only render from fundraiserProgress case, promoterProgress returns null
          return sectionId === "fundraiserProgress" ? (
            <ProgressSection
              showFundraiser={true}
              showPromoter={true}
              promoterId="mock-promoter"
              forceStacked={isInRightZone}
              fundraiser={builderData}
            />
          ) : null;
        } else if (
          sectionVisibility.fundraiserProgress &&
          sectionId === "fundraiserProgress"
        ) {
          return (
            <ProgressSection
              showFundraiser={true}
              showPromoter={false}
              fundraiser={builderData}
            />
          );
        } else if (
          sectionVisibility.promoterProgress &&
          sectionId === "promoterProgress"
        ) {
          return (
            <ProgressSection
              showFundraiser={false}
              showPromoter={true}
              promoterId="mock-promoter"
              fundraiser={builderData}
            />
          );
        }
        return null;

      case "stats":
        if (builderData.fundraiserStats.length === 0) {
          return null;
        }

        return <ImpactStats forceStacked={isInRightZone} />;

      case "mission":
        if (!builderData.missionTitle && !builderData.missionDescription) {
          return null;
        }

        return <MissionSection customColors={builderData.getCustomColors()} />;

      case "impact":
        if (builderData.impact.length === 0) {
          return null;
        }

        return (
          <ImpactSection
            impacts={builderData.impact || []}
            forceStacked={isInRightZone}
            zoneType={zoneType as "full-width" | "left-column" | "right-column"}
          />
        );

      case "media":
        return (
          <MediaSection
            mediaItems={builderData.mediaItems}
            fundraiser={builderData}
            isPreviewMode={true}
            key={`media-${isDragging ? "dragging" : "stable"}`}
          />
        );

      case "description": {
        const descriptionText = builderData.description?.trim();

        if (!descriptionText) {
          return (
            <div
              style={{
                display: "none",
              }}
            />
          );
        }

        return (
          <div className="description-design-preview">
            <p
              style={{
                fontSize: "16px",
                color: "#64748b",
                lineHeight: "1.6",
                margin: 0,
              }}
            >
              {descriptionText}
            </p>
          </div>
        );
      }

      case "promoters":
        return (
          <PromoterLeaderboard leaderboard={builderData.leaderboard || []} />
        );

      case "recent":
        return <RecentDonations donations={fundraiserStore.donations} />;

      case "cta":
        // Determine if CTA is in a full-width zone for preview
        const ctaZonePreview = Object.entries(fundraiserStore.layoutZones).find(
          ([zoneKey, sections]) => sections.includes("cta")
        );
        const isCtaInFullWidthPreview =
          ctaZonePreview &&
          (ctaZonePreview[0] === "topZone" ||
            ctaZonePreview[0] === "bottomZone");

        // Determine if should show as banner (full-width AND not image mode)
        const ctaIconImageChoice = builderData?.ctaIconImageChoice || "icon"; // Default to 'icon' if not set
        const ctaImage = builderData?.ctaImage;
        // Only disable banner mode when there's both image selection AND actual image
        const isImageMode = ctaIconImageChoice === IconType.Image && !!ctaImage;
        const shouldShowAsBanner = isCtaInFullWidthPreview && !isImageMode;

        return (
          <CallToAction
            onDonateClick={mockDonateClick}
            isPreview={true}
            isBanner={shouldShowAsBanner}
            customColors={builderData.getCustomColors()}
            buttonText={builderData?.ctaButtonText || "Donate"}
            showIcon={ctaIconImageChoice !== IconType.None}
            showButtonOnImage={builderData?.showCtaButton !== false}
            isFullWidth={isCtaInFullWidthPreview}
          />
        );

      case "donation":
        if (donationFormType === DonationFormType.Embedded) {
          return (
            <DonationCapture
              isVisible={true}
              onClose={() => {}}
              isEmbedded={true}
              shortUniqueId={AuthService.shortUniqueId}
            />
          );
        }
        return null;

      case "sponsor":
        if (fundraiserStore.builderData?.bannerImage.fullURL) {
          return (
            <div className="sponsor-design-preview">
              <img
                src={fundraiserStore.builderData.bannerImage.fullURL}
                alt="Sponsor Banner"
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #e2e8f0",
                }}
              />
            </div>
          );
        }
        return null;

      default:
        return null;
    }
  };

  const renderZone = (
    zoneId: keyof typeof fundraiserStore.layoutZones,
    zoneSections: string[]
  ) => {
    const zoneType =
      zoneId.includes("top") || zoneId.includes("bottom")
        ? "full-width"
        : zoneId.includes("left")
          ? "left-column"
          : "right-column";

    // Create a stable key for the zone to prevent unnecessary re-renders
    const zoneKey = `${zoneId}-${zoneSections.join("-")}`;

    // Check if this zone should be visible based on layout configuration
    const zoneWidth = zoneWidths[zoneId as keyof typeof zoneWidths] || 0;
    const isZoneActive = zoneWidth > 0;

    // Count sections that actually render (not null) - fix for donation popup issue
    // Treat description as visible even if empty, to avoid unwanted drop zone
    const visibleSectionsCount = zoneSections.filter((sectionId) => {
      if (sectionId === "description") return true; // Always count description as visible
      const renderedSection = renderSection(sectionId, zoneType);
      return renderedSection !== null;
    }).length;

    const shouldShowDropZone =
      isZoneActive && visibleSectionsCount === 0 && zoneSections.length === 0;

    return (
      <React.Fragment key={zoneKey}>
        {/* Always show drop zone if zone is active and empty - primary fix for bug 1 */}
        {shouldShowDropZone && (
          <DropZone slotId={zoneId} slotType={zoneType} isEmpty={true} />
        )}

        {/* Render sections in this zone */}
        {zoneSections.map((sectionId, index) => {
          const renderedSection = renderSection(sectionId, zoneType);

          // Skip rendering if section returns null
          if (!renderedSection) return null;

          return (
            <DraggableSection
              key={`${sectionId}-${isDragging ? "drag" : "normal"}`}
              sectionId={sectionId}
              sectionName={getSectionName(sectionId)}
              zoneType={zoneType}
              isActive={activeId === sectionId}
              isDragMode={isDragging}
              onHide={() => {
                // Special logic for progress sections when both are visible
                if (
                  (sectionId === "fundraiserProgress" ||
                    sectionId === "promoterProgress") &&
                  sectionVisibility.fundraiserProgress !== false &&
                  sectionVisibility.promoterProgress !== false
                ) {
                  // Hide both progress sections when either is hidden and both are currently visible
                  const updated = {
                    ...sectionVisibility,
                    fundraiserProgress: false,
                    promoterProgress: false,
                  };
                  fundraiserStore.updateBuilderField(
                    "sectionVisibility",
                    updated
                  );
                } else {
                  // Normal hide behavior for individual sections
                  const updated = {
                    ...sectionVisibility,
                    [sectionId]: false,
                  };
                  fundraiserStore.updateBuilderField(
                    "sectionVisibility",
                    updated
                  );
                }
              }}
            >
              {renderedSection}
            </DraggableSection>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <PreviewContainer>
      <PreviewFrame>
        <PreviewContent>
          <PreviewWrapper
            key={
              builderData.getCustomColors().background.rgba ||
              builderData.getCustomColors().background.hex
            }
            backgroundColor={
              builderData.getCustomColors().background.rgba ||
              builderData.getCustomColors().background.hex
            }
          >
            <SectionContainer
              backgroundColor={
                builderData.getCustomColors().background.rgba ||
                builderData.getCustomColors().background.hex
              }
            >
              <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={getAllDraggableSections()}
                  strategy={verticalListSortingStrategy}
                >
                  {/* Fixed Header */}
                  <div style={{ position: "sticky", top: 0, zIndex: 50 }}>
                    {sectionVisibility.header !== false && (
                      <DraggableSection
                        sectionId="header"
                        sectionName="Header"
                        disabled={true}
                        zoneType="full-width"
                        isDragMode={isDragging}
                        onHide={() => {
                          // Header cannot be hidden
                        }}
                      >
                        {renderSection("header")}
                      </DraggableSection>
                    )}
                  </div>

                  <ContentWrapper>
                    {/* Top Zone - Full Width */}
                    {zoneWidths.topZone > 0 && (
                      <TopZone
                        style={{
                          display: "flex",
                          width: "100%",
                          minHeight:
                            topZoneSections.length === 0 ? "60px" : "auto",
                        }}
                      >
                        {renderZone("topZone", topZoneSections)}
                      </TopZone>
                    )}

                    {/* Middle Zone - Two Columns */}
                    {(zoneWidths.leftZone > 0 || zoneWidths.rightZone > 0) && (
                      <MiddleZoneContainer
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            zoneWidths.leftZone === 0
                              ? "1fr"
                              : zoneWidths.rightZone === 0
                                ? "1fr"
                                : `${zoneWidths.leftZone}fr ${zoneWidths.rightZone}fr`,
                        }}
                      >
                        {zoneWidths.leftZone > 0 && (
                          <LeftZone
                            style={{
                              display: "flex",
                              minHeight:
                                leftZoneSections.length === 0
                                  ? "200px"
                                  : "auto",
                            }}
                          >
                            {renderZone("leftZone", leftZoneSections)}
                          </LeftZone>
                        )}

                        {zoneWidths.rightZone > 0 && (
                          <RightZone
                            style={{
                              display: "flex",
                              minHeight:
                                rightZoneSections.length === 0
                                  ? "200px"
                                  : "auto",
                            }}
                          >
                            {renderZone("rightZone", rightZoneSections)}
                          </RightZone>
                        )}
                      </MiddleZoneContainer>
                    )}

                    {/* Bottom Zone - Full Width */}
                    {zoneWidths.bottomZone > 0 && (
                      <BottomZone
                        style={{
                          display: "flex",
                          width: "100%",
                          minHeight:
                            bottomZoneSections.length === 0 ? "60px" : "auto",
                        }}
                      >
                        {renderZone("bottomZone", bottomZoneSections)}
                      </BottomZone>
                    )}
                  </ContentWrapper>
                </SortableContext>

                <DragOverlay>
                  {activeId ? (
                    <SectionDragPreview>
                      <SectionPreviewLabel>
                        {getSectionName(activeId)}
                      </SectionPreviewLabel>
                      <SectionPreviewContent>
                        {renderSection(activeId, "full-width")}
                      </SectionPreviewContent>
                    </SectionDragPreview>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </SectionContainer>
          </PreviewWrapper>
        </PreviewContent>
      </PreviewFrame>
    </PreviewContainer>
  );
});
